import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { getPollResults } from '../../../services/participantService';

const PollContent = ({ question, options, onVote, pollDate }) => {
  const [selectedOption, setSelectedOption] = useState('');
  const [hasVoted, setHasVoted] = useState(false);
  const [results, setResults] = useState({});
  const [totalVotes, setTotalVotes] = useState(0);
  const [loadingResults, setLoadingResults] = useState(false);

  const defaultOptions = [
    'Emniyet kemeri takmak',
    'Hız limitine uymak',
    'Kırmızı ışıkta durmak',
    'Yaya geçidine saygı göstermek'
  ];

  const pollOptions = options || defaultOptions;

  const handleVote = async () => {
    if (!selectedOption || hasVoted) return;

    if (onVote) {
      onVote(selectedOption);
    }

    setLoadingResults(true);

    // Small delay to allow the vote to be saved before fetching results
    await new Promise((resolve) => setTimeout(resolve, 600));

    const { data } = await getPollResults(pollDate);

    if (data) {
      const { voteCounts, totalVotes: total } = data;

      // Include the current vote in case it hasn't been saved yet
      const updatedCounts = { ...voteCounts };
      updatedCounts[selectedOption] = (updatedCounts?.[selectedOption] || 0) + 1;
      const updatedTotal = total + (voteCounts?.[selectedOption] !== undefined ? 0 : 1);
      const finalTotal = Math.max(updatedTotal, total + 1);

      const percentages = {};
      pollOptions?.forEach((option) => {
        const count = updatedCounts?.[option] || 0;
        percentages[option] = finalTotal > 0 ? Math.round((count / finalTotal) * 100) : 0;
      });

      setResults(percentages);
      setTotalVotes(finalTotal);
    }

    setLoadingResults(false);
    setHasVoted(true);
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary/10">
          <Icon name="BarChart3" size={24} color="var(--color-primary)" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground mb-3 md:mb-4">
            Günün Anketi
          </h3>
          <p className="text-base md:text-lg lg:text-xl text-foreground leading-relaxed">
            {question || 'Sence en önemli trafik kuralı hangisi?'}
          </p>
        </div>
      </div>
      <div className="space-y-3 md:space-y-4">
        {pollOptions?.map((option, index) => (
          <button
            key={index}
            onClick={() => !hasVoted && setSelectedOption(option)}
            disabled={hasVoted}
            className={`w-full p-4 md:p-5 lg:p-6 rounded-lg border-2 transition-all duration-250 text-left ${
              selectedOption === option
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 bg-card'
            } ${hasVoted ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-foreground text-sm md:text-base lg:text-lg">
                {option}
              </span>
              {hasVoted && results?.[option] !== undefined && (
                <span className="text-primary font-bold text-sm md:text-base">
                  %{results?.[option]}
                </span>
              )}
            </div>
            {hasVoted && results?.[option] !== undefined && (
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${results?.[option]}%` }}
                />
              </div>
            )}
          </button>
        ))}
      </div>
      <Button
        variant={hasVoted ? 'success' : 'default'}
        size="lg"
        fullWidth
        onClick={handleVote}
        disabled={!selectedOption || hasVoted || loadingResults}
        iconName={hasVoted ? 'CheckCircle' : loadingResults ? 'Loader' : 'Vote'}
        iconPosition="right"
      >
        {hasVoted ? 'Oy Kullanıldı' : loadingResults ? 'Sonuçlar yükleniyor...' : 'Oy Kullan'}
      </Button>
      {hasVoted && (
        <p className="text-center text-sm md:text-base text-muted-foreground flex items-center justify-center gap-2">
          <Icon name="Users" size={16} />
          Toplam {totalVotes} kişi oy kullandı
        </p>
      )}
    </div>
  );
};

export default PollContent;